import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Transient multi-selection within ONE freeform column, used to build a group.
 * Lives only in the editor UI (never persisted / synced). Persistent grouping
 * is the `element.groupId` written by `groupFreeformElements`.
 */
interface FreeformGroupCtx {
  selected: Set<string>;
  toggle(id: string): void;
  clear(): void;
}

const Ctx = createContext<FreeformGroupCtx | null>(null);

export function FreeformGroupProvider({ children }: { children: ReactNode }) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const value = useMemo<FreeformGroupCtx>(
    () => ({
      selected,
      toggle: (id) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }),
      clear: () => setSelected(new Set()),
    }),
    [selected],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Returns the group context, or null when rendered outside a provider
 *  (teach/preview canvases) so freeform elements still work. */
export function useFreeformGroupMaybe(): FreeformGroupCtx | null {
  return useContext(Ctx);
}

const GROUP_COLORS = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#3b82f6',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
];

/** Stable color for a group badge, hashed from the groupId. */
export function groupColor(groupId: string): string {
  let h = 0;
  for (let i = 0; i < groupId.length; i++) h = ((h << 5) - h + groupId.charCodeAt(i)) | 0;
  return GROUP_COLORS[Math.abs(h) % GROUP_COLORS.length];
}
